import React, {useState} from 'react';
import {inject, observer} from 'mobx-react';
import CourseLearnerUI from '../view/CourseLearnerUI';
import CourseLearnerStore from '../store/CourseLearnerStore';
import {useEffect} from 'react';
import {useOutletContext, useParams} from 'react-router-dom';
import {Course} from '../../../../data/models/Course/Course';
import {LearnerListItem} from '../../../../data/models/Learners/Learner';
import {useDebouncedSearch} from '../../../../utils/useDebouncedSearch';
import LearnerGroupsStore from '../../../../modules/LearnerGroups/store/LearnerGroupsStore';

interface Props {
  courseLearnerStore?: CourseLearnerStore;
  learnerGroupsStore?: LearnerGroupsStore;
}

export const ENROLLMENT_LIST_PAGE_SIZE = 10;
export const ADD_LEARNERS_TAB = 'add';
export const ENROLLED_LEARNERS_TAB = 'enrolled';

const CourseLearnerContainer = inject(
  'courseLearnerStore',
  'learnerGroupsStore',
)(
  observer(({courseLearnerStore, learnerGroupsStore}: Props) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pages, setPages] = useState(0);
    const {organizationId} = useParams();
    const [learnerData, setLearnerData] = React.useState<LearnerListItem[]>([]);
    const [search, setSearch] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [enrolledTotal, setEnrolledTotal] = useState(0);
    const [isEnrolling, setIsEnrolling] = useState(false);
    const [tab, setTab] = useState(ENROLLED_LEARNERS_TAB);

    const context = useOutletContext() as [any, any, Course];
    const debouncedSearch = useDebouncedSearch(search);

    useEffect(() => {
      setCurrentPage(1);
    }, [debouncedSearch]);

    useEffect(() => {
      if (organizationId) {
        learnerGroupsStore!.fetchLearnerGroupNames(organizationId);
      }
    }, [organizationId]);

    useEffect(() => {
      fetchLeaners();
      fetchEnrolledLearnersCount();
      fetchCourse(context[2]?.courseId || '');
    }, [courseLearnerStore, currentPage, debouncedSearch, tab, context]);

    const fetchCourse = (courseId: string) => {
      if (organizationId && courseId) {
        courseLearnerStore!.getCourse(organizationId, courseId).then(course => {
          context[1](course?.title || '');
        });
      }
    };

    const fetchLeaners = async () => {
      if (organizationId && context[2]) {
        setIsLoading(true);
        try {
          const res =
            tab === ENROLLED_LEARNERS_TAB
              ? await courseLearnerStore!.getEnrolledLearners(
                  organizationId,
                  context[2].id || '',
                  currentPage - 1,
                  ENROLLMENT_LIST_PAGE_SIZE,
                  debouncedSearch,
                )
              : await courseLearnerStore!.getOrganizationLearners(
                  organizationId,
                  context[2].id || '',
                  currentPage - 1,
                  ENROLLMENT_LIST_PAGE_SIZE,
                  debouncedSearch,
                );
          setLearnerData(res.data || []);
          setPages(res.pagesCount);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    const fetchEnrolledLearnersCount = async () => {
      if (organizationId && context[2]) {
        const pagesCount = await courseLearnerStore!.getEnrolledLearnersCount(
          organizationId,
          context[2].id || '',
        );
        setEnrolledTotal(pagesCount);
      } else {
        setEnrolledTotal(0);
      }
    };

    const onSave = async (selectedLearners: string[], dueDate?: Date) => {
      if (organizationId && context[2]) {
        setIsEnrolling(true);
        try {
          const success = await courseLearnerStore!.saveLearners(
            selectedLearners,
            organizationId,
            context[2].id || '',
            dueDate,
          );
          if (success) {
            resetData();
            fetchLeaners();
            fetchEnrolledLearnersCount();
          }
        } finally {
          setIsEnrolling(false);
        }
      }
    };

    const onRemove = async (selectedLearners: string[]) => {
      if (organizationId && context[2]) {
        setIsEnrolling(true);
        try {
          const success = await courseLearnerStore!.removeLearnersFromCourse(
            organizationId,
            context[2].id || '',
            selectedLearners,
          );
          if (success) {
            resetData();
          }
          fetchLeaners();
          fetchEnrolledLearnersCount();
        } finally {
          setIsEnrolling(false);
        }
      }
    };

    const onEnrollGroup = async (groupIds: string[], dueDate?: Date) => {
      if (organizationId && context[2]) {
        setIsEnrolling(true);
        try {
          await learnerGroupsStore!.enrollGroups(
            organizationId,
            groupIds,
            context[2].id || '',
            dueDate,
          );
          resetData();
          fetchLeaners();
          fetchEnrolledLearnersCount();
        } finally {
          setIsEnrolling(false);
        }
      }
    };

    const onTabChange = (nextTab: string) => {
      setTab(nextTab);
      setSearch('');
      setCurrentPage(1);
      setLearnerData([]);
    };

    const resetData = () => {
      setCurrentPage(1);
      setLearnerData([]);
    };

    const onPageChange = (page: number) => {
      setCurrentPage(page);
    };

    return (
      <CourseLearnerUI
        learnerData={learnerData}
        onSave={onSave}
        pages={pages}
        onPageChange={onPageChange}
        search={search}
        onSearchChange={setSearch}
        currentPage={currentPage}
        publishedCourse={context[2]}
        tab={tab}
        onTabChange={onTabChange}
        enrolledTotal={enrolledTotal}
        onRemove={onRemove}
        isLoading={isLoading}
        isEnrolling={isEnrolling}
        groups={learnerGroupsStore!.groups}
        groupNames={learnerGroupsStore!.learnerGroupNames}
        onEnrollGroup={onEnrollGroup}
      />
    );
  }),
);
export default CourseLearnerContainer;
